import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { HomeData } from "../use-home-data";

type MenuGrowthChartProps = {
  chart: HomeData["chart"];
};

const MAX_TICKS = 6;

export function MenuGrowthChart({ chart }: MenuGrowthChartProps) {
  const { t, i18n } = useTranslation();

  const chartConfig = useMemo<ChartConfig>(
    () => ({
      count: {
        label: t("dashboard.chart.productsAdded"),
        color: "var(--chart-1)",
      },
    }),
    [t],
  );

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" }),
    [i18n.language],
  );

  const total = chart.reduce((sum, point) => sum + point.count, 0);
  const interval = Math.max(1, Math.floor(chart.length / MAX_TICKS));

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{t("dashboard.chart.title")}</CardTitle>
        <CardDescription>{t("dashboard.chart.subtitle", { count: total })}</CardDescription>
      </CardHeader>
      <div className="px-2 pb-4 sm:px-6">
        <ChartContainer className="aspect-auto h-[260px] w-full" config={chartConfig}>
          <AreaChart accessibilityLayer data={chart} margin={{ left: 4, right: 12, top: 8 }}>
            <defs>
              <linearGradient id="menu-growth-fill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="5%" stopColor="var(--color-count)" stopOpacity={0.7} />
                <stop offset="95%" stopColor="var(--color-count)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="date"
              interval={interval}
              minTickGap={24}
              tickFormatter={(value: string) => dateFormatter.format(new Date(value))}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              width={24}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => dateFormatter.format(new Date(value as string))}
                />
              }
              cursor={false}
            />
            <Area
              dataKey="count"
              fill="url(#menu-growth-fill)"
              stroke="var(--color-count)"
              strokeWidth={2}
              type="monotone"
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </Card>
  );
}
